import { BreadcrumbProps } from "@/shared/navigation/breadcrumbs";
import { BreadcrumbsOptions } from "../models/types";
import { useTranslations } from "use-intl";
import { matchRoutes, PathParam, useMatches } from "react-router-dom";
import { capitalize } from "@/shared/typography/text/helpers";
import { moduleRoutes } from "@/application/routes/routes";

type Item = NonNullable<BreadcrumbProps["items"]>[number];

export function useBreadcrumbs<T extends string>(
  options?: BreadcrumbsOptions<T>,
) {
  const match = useMatches()[0];
  const t = useTranslations("Breadcrumbs");

  const items: Item[] = (options?.path.split("/") ?? [])
    .filter(Boolean)
    .reduce(
      (
        result: Array<{
          title: string;
          href: string;
          rawHref: string;
        }>,
        segment,
      ) => {
        const last = result.at(-1);

        const paramName = segment.startsWith(":")
          ? (segment.slice(1).replace(/\?$/, "") as PathParam<T>)
          : null;

        const paramEntityName =
          paramName && options && "entityNames" in options
            ? options.entityNames[paramName]
            : null;

        const path = paramName ? match.params[paramName] : segment;
        const title = paramEntityName ?? t(capitalize(segment));

        const href = `${last?.rawHref ?? ""}/${path}`;

        const found =
          matchRoutes(moduleRoutes.at(1)?.children ?? [], href)?.[0].route
            .path !== "*";

        result.push({
          title,
          href: found ? href : (last?.href ?? ""),
          rawHref: href,
        });

        return result;
      },
      [],
    )
    .map(({ title, href }) => ({ href, title }));

  return {
    items,
  };
}
